import {
  ColumnDef,
  OnChangeFn,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  Table as TableComponent,
  TableOptions,
  Cell,
  RowSelection,
  RowSelectionState,
  VisibilityState,
  Header
} from "@tanstack/react-table";
import classNames from "classnames";
import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Link } from "react-router-dom";
import Icon from "../Icon";
import { MaterialNames } from "../material_symbol_names";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useTableKeyboardNavigation } from "@/hooks/useTableKeyboardNavigation";
import { SortingFn } from "@tanstack/table-core";
import { TableBodyMobile } from "./TableBodyMobile";
import { SkeletonLoader } from "./SkeletonLoader";
import { TableBody } from "./TableBody";
import { customAlphanumericSorting } from "./utils";
import { isEmpty } from "lodash";
import EmptyState from "../EmptyState";
import MobileSortSideSheet, { SortOption } from "./MobileSortSideSheet";
import { createSortOptionsFromColumns, SortFieldType } from "./sortUtils";
import type { EditableSelectOption } from "./EditableSelectCell";

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends unknown, TValue> {
    editable?: boolean | ((row: Row<TData>) => boolean);
    onEdit?: (value: string, cell: Cell<TData, TValue>) => void;
    editableClassName?: string;
    editableIcon?: (cell: Cell<TData, TValue>) => React.ReactNode;
    editableInputType?: "currency" | "default" | "select";
    editableSelectOptions?: EditableSelectOption[];
    skipEmptyState?: boolean;
    sortType?: SortFieldType;
  }

  interface TableMeta<TData extends unknown> {
    editableRowIndex?: number | null;
    setEditableRowIndex?: (index: number | null) => void;
    editableCellId?: string | null;
    setEditableCellId?: (id: string | null) => void;
    useAltBorder?: boolean;
    onClickRow?: (row: Row<TData>, event?: React.MouseEvent) => void;
    isCompactLayout?: boolean;
  }
}

declare module "@tanstack/table-core" {
  interface SortingFns {
    customAlphanumericSorting: SortingFn<unknown>;
  }
}

export type { Row };

export type SharedProps<T> = {
  renderExpandedRow?: (props: { row: Row<T> }) => React.ReactElement;
  onClickRow?: (row: Row<T>, event?: React.MouseEvent) => void;
  getRowLink?: (row: Row<T>) => React.ComponentProps<typeof Link> | null | undefined;
};

/**
 * Handler to open the mobile sort side sheet (if columns with sortType are provided)
 */
export type TableRenderHelpers = {
  onSortClick?: () => void;
};

/**
 * Props for the Table component
 *
 * @property renderControls - Renders control row (search, filters, bulk actions) above table.
 *   Typically contains TableControls component with search, filters, and pagination.
 *   **Note:** Controls are automatically hidden when showing the "no data" empty state (renderNoDataEmptyState).
 *
 * @example Basic usage:
 * ```tsx
 * const { createHeaderPagination } = usePagination();
 * <Table
 *   renderControls={() => (
 *     <TableControls pagination={createHeaderPagination(data)}>
 *       <SearchInput />
 *     </TableControls>
 *   )}
 * />
 * ```
 *
 * @example With automatic mobile sorting via MobileSortSideSheet:
 * ```tsx
 * const columns = [
 *   {
 *     accessorKey: "name",
 *     header: "Name",
 *     meta: { sortType: "text" }  // Automatically enables sorting (mobile + desktop)
 *   },
 *   {
 *     accessorKey: "date",
 *     header: "Date",
 *     meta: { sortType: "date" }  // Automatically enables sorting (mobile + desktop)
 *   }
 * ];
 *
 * <Table
 *   columns={columns}
 *   sortingState={sorting}
 *   onSortingChange={setSorting}
 *   renderControls={(_table, { onSortClick }) => (
 *     <TableControls onSortClick={onSortClick}>
 *       <SearchInput />
 *     </TableControls>
 *   )}
 * />
 * // Note: enableSorting defaults to true when sortType is provided. The mobile
 * // sheet mounts only when sortingState and onSortingChange are also passed.
 * ```
 *
 * @example With bulk selection:
 * ```tsx
 * const { bulkSelectionProps } = useBulkSelection();
 * <Table
 *   renderControls={(table) => (
 *     <TableControls
 *       pagination={createHeaderPagination(data)}
 *       bulkActions={[{ label: "Delete", icon: "delete", onClick: handleDelete }]}
 *       {...bulkSelectionProps}
 *       onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
 *       allRowsSelected={table.getIsAllRowsSelected()}
 *       someRowsSelected={table.getIsSomeRowsSelected()}
 *     >
 *       <SearchInput />
 *     </TableControls>
 *   )}
 * />
 * ```
 *
 * @property renderHeader - **@deprecated** Use renderControls instead. renderHeader will be removed in v2.0.
 *   Renders control row (search, filters, bulk actions) above table.
 *
 * @property renderFooter - Renders footer content inside Table component.
 *   **Best Practice:** Always use the TableFooter component for consistent styling
 *   and structure across all tables, whether pagination is present or not.
 *
 * @example With pagination:
 * ```tsx
 * const { createFooterPagination } = usePagination();
 * <Table
 *   renderFooter={() => <TableFooter pagination={createFooterPagination(data)} />}
 * />
 * ```
 *
 * @example Without pagination:
 * ```tsx
 * <Table
 *   renderFooter={() => <TableFooter totalRowCount={data.length} />}
 * />
 * ```
 *
 * @example With bulk selection:
 * ```tsx
 * const { bulkSelectionProps } = useBulkSelection();
 * <Table
 *   renderFooter={() => (
 *     <TableFooter
 *       pagination={createFooterPagination(data)}
 *       {...bulkSelectionProps}
 *     />
 *   )}
 * />
 * ```
 *
 * @property renderFilteredEmptyState - Optional. Renders when data is empty due to active filters/search.
 *   Controls (search/filters) remain visible. Use for temporary "no results" states.
 *   If not provided, defaults to a generic "No Results Found" message with search icon.
 *   This is shown when data is empty AND hasActiveFilters is true.
 *
 * @property renderNoDataEmptyState - Optional. Component to render when there are no possible results.
 *   **This should be provided in-combination with the hasActiveFilters boolean for dynamic controls.**
 *   A table with no active filters and no results will render this empty state.
 *   If provided without hasActiveFilters, this empty state will take precedence over renderFilteredEmptyState or default.
 *   If provided with hasActiveFilters, this empty state will only show when hasActiveFilters === false.
 *
 *   **Value options:**
 *   - Function returning ReactElement: Custom empty state component
 *   - Not provided/undefined: Shows filtered empty state instead
 */
export interface Props<T> extends SharedProps<T> {
  className?: string;
  loading: boolean;
  data: T[];
  columns: ColumnDef<T, any>[];
  renderDetails?: () => React.ReactElement;
  renderHeader?: (table: TableComponent<T>) => React.ReactElement | null;
  renderControls?: (table: TableComponent<T>, helpers: TableRenderHelpers) => React.ReactElement | null;
  renderFooter?: () => React.ReactElement | null;
  serverSideSorting?: boolean;
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  renderFilteredEmptyState?: () => React.ReactElement;
  hasActiveFilters?: boolean;
  renderNoDataEmptyState?: () => React.ReactElement;
  enableRowSelection?: TableOptions<T>["enableRowSelection"];
  rowSelection?: typeof RowSelection;
  onRowSelectionChange?: TableOptions<T>["onRowSelectionChange"];
  setSelectedData?: any;
  getRowId?: (row: T) => string;
  getRowCanExpand?: (row: Row<T>) => boolean;
  onHorizontalScroll?(): void;
  ["data-testid"]?: string;
  getRowClasses?: (row: Row<T>) => string;
  columnVisibility?: VisibilityState;
  useAltBorder?: boolean;
  collapsed?: boolean;
  /**
   * Page-level j/k/o/Enter/Space row shortcuts. On by default for the sole
   * page table; nested details panels (`useAltBorder`) are excluded. Loading
   * and collapsed tables still occupy the sole-table registry so a sibling
   * table does not become the shortcut target. Enter or Escape in page search
   * selects the first row without opening, including while loading. A following
   * Enter, `o`, or Space opens or previews that highlighted row. If filtered
   * rows replace after debounce or a loading fetch, the first of those rows is
   * selected once. Persist stays until that first non-empty row-id change (an
   * empty list during debounce / Apollo variable change does not count;
   * refetch may keep loading false) or a 5s fallback so a later sort does not
   * snap to row 1. Space previews via
   * `expand-details` or is a no-op; it never scrolls the page. Enter/Space stay
   * with focused native controls and any Material `md-*` host (filter chips,
   * pagination). `j`/`k`/`o` still move and open when those hosts keep focus.
   */
  keyboardNavigation?: boolean;
}

export interface RowType {
  id: string;
}

/**
 * Isolates Table-owned state (horizontal scroll, editable row, mobile
 * sort) from the details slot so Material `Button`s in `renderDetails`
 * keep their click handlers. Inline `renderDetails={() => …}` still
 * changes identity when the *page* re-renders; this memo only helps
 * when Table re-renders for its own state.
 */
const TableDetailsRegion = React.memo(function TableDetailsRegion({
  renderDetails
}: {
  renderDetails?: () => React.ReactNode;
}) {
  return <>{renderDetails?.()}</>;
});

/**
 * Keyboard highlight lives here so `j`/`k` `activeRowId` updates do not
 * re-render the details slot. Pair with `TableDetailsRegion`: memo
 * covers Table-owned state; this child covers keyboard state.
 */
function TableKeyboardScope<T>({
  enabled,
  keysActive,
  rows,
  onClickRow,
  containerRef,
  children
}: {
  enabled: boolean;
  keysActive: boolean;
  rows: Row<T>[];
  onClickRow?: (row: Row<T>, event?: React.MouseEvent) => void;
  containerRef: React.RefObject<HTMLElement | null>;
  children: (activeRowId: string | null) => React.ReactNode;
}) {
  const { activeRowId } = useTableKeyboardNavigation({
    enabled,
    keysActive,
    rows,
    onClickRow,
    containerRef
  });
  return children(activeRowId);
}

export default function Table<T extends RowType>({
  className,
  data,
  loading,
  columns,
  sortingState,
  rowSelection,
  serverSideSorting,
  onSortingChange,
  renderExpandedRow,
  renderDetails,
  renderHeader,
  renderControls,
  renderFooter,
  onClickRow,
  getRowLink,
  renderFilteredEmptyState,
  hasActiveFilters,
  renderNoDataEmptyState,
  enableRowSelection,
  onRowSelectionChange,
  setSelectedData,
  onHorizontalScroll,
  columnVisibility,
  getRowCanExpand: getRowCanExpandProp,
  ["data-testid"]: testId,
  getRowClasses: getRowClasses$,
  useAltBorder,
  collapsed = false,
  keyboardNavigation = true
}: Props<T>) {
  const isEmptyState = !data.length && !loading;
  const { isCompactLayout } = useIsCompactLayout();
  const [editableRowIndex, setEditableRowIndex] = useState<number | null>(null);
  const [editableCellId, setEditableCellId] = useState<string | null>(null);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);
  const [isHorizontallyScrollable, setIsHorizontallyScrollable] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isScrollableRef = useRef(false);

  useLayoutEffect(() => {
    if (isCompactLayout) {
      if (isScrollableRef.current) {
        isScrollableRef.current = false;
        setIsHorizontallyScrollable(false);
      }
      return;
    }

    const container = scrollContainerRef.current;
    if (container) {
      const isScrollable = container.scrollWidth > container.clientWidth;
      if (isScrollableRef.current !== isScrollable) {
        isScrollableRef.current = isScrollable;
        setIsHorizontallyScrollable(isScrollable);
      }
    }
  }, [data, columns, isCompactLayout]);

  useEffect(() => {
    if (isCompactLayout) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScrollable = () => {
      const isScrollable = container.scrollWidth > container.clientWidth;
      if (isScrollableRef.current !== isScrollable) {
        isScrollableRef.current = isScrollable;
        setIsHorizontallyScrollable(isScrollable);
      }
    };

    const resizeObserver = new ResizeObserver(checkScrollable);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [data, columns, isCompactLayout]);

  const processedColumns = columns.map((column) => {
    if (column.meta?.sortType && column.enableSorting !== false) {
      return { ...column, enableSorting: true };
    }
    return column;
  });

  const sortOptions: SortOption[] = createSortOptionsFromColumns(processedColumns);
  const hasSortableColumns = sortOptions.length > 0;

  const defaultNoDataEmptyState = () => (
    <EmptyState iconName="scan_delete" title="No Data" caption="There are no records to display at this time" />
  );

  const defaultFilteredEmptyState = () => (
    <EmptyState iconName="search" title="No Results Found" caption="Try a different search or filter" />
  );

  const shouldShowNoDataState = isEmptyState && !hasActiveFilters;
  const shouldHideControls = shouldShowNoDataState;

  const getEmptyStateRenderer = () => {
    if (shouldShowNoDataState) {
      const renderer = renderNoDataEmptyState || defaultNoDataEmptyState;
      return renderer();
    }

    const renderer = renderFilteredEmptyState || defaultFilteredEmptyState;
    return renderer();
  };

  const effectiveColumnVisibility = {
    ...columnVisibility,
    ...(shouldShowNoDataState && { select: false })
  };

  const table = useReactTable<T>({
    data,
    columns: isEmptyState && isCompactLayout ? processedColumns.slice(0, 3) : processedColumns,
    getRowCanExpand: getRowCanExpandProp || (() => true),
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: sortingState,
      rowSelection: rowSelection as RowSelectionState,
      columnVisibility: effectiveColumnVisibility
    },
    onSortingChange,
    onRowSelectionChange,
    manualSorting: serverSideSorting,
    getSortedRowModel: serverSideSorting ? undefined : getSortedRowModel(),
    enableSortingRemoval: false,
    enableRowSelection,
    getRowId: (row: T) => row.id,
    meta: {
      editableRowIndex,
      setEditableRowIndex,
      editableCellId,
      setEditableCellId,
      useAltBorder,
      onClickRow,
      isCompactLayout
    },
    sortingFns: {
      customAlphanumericSorting
    }
  });

  function sortIcon(sort: string): MaterialNames {
    return sort === "asc" ? "arrow_upward" : "arrow_downward";
  }

  // if there isn't a footer the last row should have a bottom padding
  const getRowClasses = (row: Row<T>) => classNames(getRowClasses$?.(row), { ["group-last:h-[60px]"]: !renderFooter });

  const onClickBodyRow = (row: Row<T>) => (event?: React.MouseEvent) => {
    if (event?.target && (event.target as HTMLElement).closest('[data-source-link="true"]')) {
      return;
    }
    onClickRow?.(row, event);
  };

  useEffect(() => {
    if (setSelectedData) {
      setSelectedData(table.getSelectedRowModel().rows.map((row) => row.original));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const renderHelpers: TableRenderHelpers = {
    onSortClick: hasSortableColumns ? () => setIsMobileSortOpen(true) : undefined
  };

  const headerContent = renderControls?.(table, renderHelpers) ?? renderHeader?.(table);
  const footerContent = !isEmptyState && renderFooter ? renderFooter() : null;

  const isEmptyFragment =
    headerContent?.type === Fragment && (!headerContent.props.children || headerContent.props.children?.length === 0);

  const shouldShowHeaderContent = headerContent && !isEmptyFragment && !shouldHideControls;

  // --- Conditional CSS Class Logic ---
  const getBorderColorClass = () => {
    if (useAltBorder) {
      return "border-sys-brand-surface-container-highest";
    }
    return "border-extended-pgadarknavy-brand-color";
  };

  const containerClasses = classNames("bg-sys-brand-surface", {
    "max-lg:w-[calc(100%+2rem)] max-lg:ml-[-1rem]": isCompactLayout,
    "lg:max-w-full lg:border lg:rounded-lg": true,

    [getBorderColorClass()]: !isCompactLayout
  });

  return (
    <>
      <TableDetailsRegion renderDetails={renderDetails} />
      <TableKeyboardScope
        enabled={keyboardNavigation && !useAltBorder}
        keysActive={!collapsed && !loading}
        rows={table.getRowModel().rows}
        onClickRow={onClickRow}
        containerRef={scrollContainerRef}
      >
        {(activeRowId) => (
          <div className={classNames(containerClasses, { hidden: collapsed })}>
            <div className={classNames("w-full whitespace-nowrap", className)}>
              {shouldShowHeaderContent && (
                <div
                  data-testid="header-row"
                  className="text-left font-normal p-4 [&_div.justify-between]:items-center"
                >
                  {headerContent}
                </div>
              )}
            </div>
            <div
              ref={scrollContainerRef}
              data-testid="table-scroll-container"
              onScroll={onHorizontalScroll}
              className={classNames(
                "max-w-full",
                // Compact uses stacked cards. `overflow-x-auto` computes overflow-y
                // to auto as well, so iOS Chrome treats the full card list as one
                // compositor layer and can inflate scrollHeight until the page
                // goes white and crashes.
                { "overflow-x-auto": !isCompactLayout },
                { "rounded-t-lg": isEmpty(renderControls) && isEmpty(renderHeader) },
                { "rounded-b-lg": isEmpty(renderFooter) },
                { "table-horizontally-scrollable": !isCompactLayout && isHorizontallyScrollable }
              )}
            >
              {isCompactLayout ? (
                <>
                  {data.length && !loading ? (
                    <TableBodyMobile
                      {...{
                        table,
                        onClickBodyRow,
                        renderExpandedRow,
                        onClickRow,
                        getRowLink,
                        getRowClasses: getRowClasses$ ? getRowClasses : undefined,
                        activeRowId
                      }}
                    />
                  ) : (
                    <div>{isEmptyState ? getEmptyStateRenderer() : <SkeletonLoader />}</div>
                  )}
                </>
              ) : (
                <table data-testid={testId} className={classNames("w-full", className)}>
                  <thead className="whitespace-nowrap">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        className={classNames(
                          `min-h-[50px] border-b-2 ${getBorderColorClass()} hidden lg:table-row`,
                          "bg-sys-brand-surface"
                        )}
                        data-testid="head-row"
                      >
                        {headerGroup.headers.map((header, index) => {
                          const headerClasses = header.column.columnDef.meta?.headerClassName || "";
                          const canSort = header.column.getCanSort() && data.length > 0;
                          function handleHeaderClick(header: Header<T, unknown>) {
                            if (canSort) {
                              header.column.toggleSorting(header.column.getIsSorted() === "asc");
                            }
                          }
                          return (
                            <th
                              className={classNames(
                                "text-sys-brand-on-surface px-4 py-3",
                                "text-body-large-bold font-serif",
                                !headerClasses?.includes("justify-end") &&
                                  !headerClasses?.includes("justify-center") &&
                                  "text-left",
                                {
                                  "cursor-pointer group": canSort,
                                  [`sticky left-0 bg-sys-brand-surface border-b-2 ${getBorderColorClass()}`]:
                                    data.length && index === 0,
                                  [`sticky right-0 bg-sys-brand-surface border-b-2 ${getBorderColorClass()}`]:
                                    data.length && header.column.id === "actions"
                                },
                                headerClasses
                              )}
                              style={{
                                width: header.column.getSize(),
                                minWidth: header.column.columnDef.minSize
                              }}
                              onClick={() => handleHeaderClick(header)}
                              key={header.id}
                              colSpan={header.colSpan}
                            >
                              <div className={classNames("flex items-center gap-1", headerClasses)}>
                                {flexRender(header.column.columnDef.header, header.getContext())}

                                {canSort && (
                                  <Icon
                                    name={sortIcon(header.column.getIsSorted() as string)}
                                    size={18}
                                    className={classNames(
                                      "transition-transform duration-200 group-hover:rotate-180",
                                      !header.column.getIsSorted() ? "invisible group-hover:visible" : ""
                                    )}
                                  />
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  {data.length && !loading ? (
                    <TableBody
                      {...{
                        table,
                        onClickBodyRow,
                        renderExpandedRow,
                        onClickRow,
                        getRowLink,
                        getRowClasses: getRowClasses$ ? getRowClasses : undefined,
                        activeRowId
                      }}
                    />
                  ) : (
                    <tbody>
                      {isEmptyState ? (
                        <tr data-testid="empty-state-row">
                          <td colSpan={processedColumns.length}>{getEmptyStateRenderer()}</td>
                        </tr>
                      ) : (
                        <SkeletonLoader columns={processedColumns} />
                      )}
                    </tbody>
                  )}
                </table>
              )}
            </div>

            {!isEmptyState && footerContent && (
              <div className={classNames("w-full whitespace-nowrap", className)}>
                <div data-testid="footer-row" className="border-t p-4">
                  {footerContent}
                </div>
              </div>
            )}
          </div>
        )}
      </TableKeyboardScope>

      {hasSortableColumns && sortingState && onSortingChange && (
        <MobileSortSideSheet
          isOpen={isMobileSortOpen}
          onClose={() => setIsMobileSortOpen(false)}
          onSortChange={onSortingChange}
          currentSort={sortingState}
          sortOptions={sortOptions}
        />
      )}
    </>
  );
}
